import { numberValue } from './pricing.js';

function timestampValue(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  return numberValue(value);
}

export function summarizeInsights({ estimates = [], products = [], materials = [] } = {}) {
  const estimateTotals = estimates.reduce((summary, estimate) => {
    const inputs = estimate.inputs || {};
    const results = estimate.results || {};
    summary.revenue += numberValue(results.recommendedBatchPrice);
    summary.profit += numberValue(results.totalProfit);
    summary.productionCost += numberValue(results.totalBatchCost);
    summary.margin += numberValue(inputs.targetMargin, numberValue(results.targetMargin));
    summary.material += numberValue(results.materialCost);
    summary.electricity += numberValue(results.electricityCost);
    summary.machine += numberValue(results.machineCost);
    summary.labor += numberValue(results.laborCost);
    summary.failure += numberValue(results.failureAllowance);
    summary.fixed += numberValue(results.fixedCost);
    return summary;
  }, {
    revenue: 0,
    profit: 0,
    productionCost: 0,
    margin: 0,
    material: 0,
    electricity: 0,
    machine: 0,
    labor: 0,
    failure: 0,
    fixed: 0,
  });

  const inventory = materials.reduce((summary, material) => {
    const remaining = Math.max(0, numberValue(material.remainingGrams));
    const spoolWeight = Math.max(0, numberValue(material.spoolWeightGrams));
    const spoolPrice = Math.max(0, numberValue(material.spoolPrice));
    const threshold = Math.max(0, numberValue(material.lowStockThresholdGrams));
    summary.remainingGrams += remaining;
    summary.value += spoolWeight > 0 ? remaining * (spoolPrice / spoolWeight) : 0;
    if (remaining <= threshold) summary.lowStock += 1;
    return summary;
  }, { remainingGrams: 0, value: 0, lowStock: 0 });

  const costBreakdown = [
    { id: 'material', label: 'Material', value: estimateTotals.material },
    { id: 'electricity', label: 'Electricity', value: estimateTotals.electricity },
    { id: 'machine', label: 'Machine', value: estimateTotals.machine },
    { id: 'labor', label: 'Labor', value: estimateTotals.labor },
    { id: 'failure', label: 'Failure reserve', value: estimateTotals.failure },
    { id: 'fixed', label: 'Fixed and extra', value: estimateTotals.fixed },
  ];
  const largestCost = Math.max(1, ...costBreakdown.map((item) => item.value));

  const topEstimates = [...estimates]
    .sort((a, b) => numberValue(b.results?.totalProfit) - numberValue(a.results?.totalProfit))
    .slice(0, 5);
  const recentActivity = [...estimates]
    .sort((a, b) => timestampValue(b.updatedAt) - timestampValue(a.updatedAt))
    .slice(0, 5);

  return {
    estimateCount: estimates.length,
    productCount: products.length,
    materialCount: materials.length,
    revenue: estimateTotals.revenue,
    profit: estimateTotals.profit,
    productionCost: estimateTotals.productionCost,
    averageMargin: estimates.length ? estimateTotals.margin / estimates.length : 0,
    averageProfit: estimates.length ? estimateTotals.profit / estimates.length : 0,
    inventoryRemainingGrams: inventory.remainingGrams,
    inventoryValue: inventory.value,
    lowStockCount: inventory.lowStock,
    costBreakdown: costBreakdown.map((item) => ({
      ...item,
      percentOfLargest: (item.value / largestCost) * 100,
    })),
    topEstimates,
    recentActivity,
  };
}
